import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Paperclip, Pencil, Send, Smile, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/Popover';
import { toast } from '@/shared/ui/toastStore';
import { chatApi } from '../api/chatApi';
import type { ChatAttachment, ChatMessage } from '../model/types';
import { EMOJIS, fileSize } from './chatFormat';

interface Upload {
  id: string;
  file: File;
  progress: number;
  result?: ChatAttachment;
  error?: boolean;
  preview?: string;
}

export interface ComposerHandle {
  addFiles: (files: File[]) => void;
  focus: () => void;
}

const MAX_BYTES = 25 * 1024 * 1024;

export const Composer = forwardRef<
  ComposerHandle,
  {
    disabled?: boolean;
    replyTo: ChatMessage | null;
    editing: ChatMessage | null;
    onCancelReply: () => void;
    onCancelEdit: () => void;
    onSend: (content: string, attachments: ChatAttachment[]) => Promise<boolean>;
    onSaveEdit: (m: ChatMessage, content: string) => Promise<boolean>;
    onTyping: () => void;
  }
>(function Composer(p, ref) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const lastTyping = useRef(0);

  // Düzenlemeye girince metin mesajın içeriğiyle dolar.
  useEffect(() => {
    if (p.editing) {
      setText(p.editing.content);
      requestAnimationFrame(() => area.current?.focus());
    }
  }, [p.editing]);
  useEffect(() => {
    if (p.replyTo) area.current?.focus();
  }, [p.replyTo]);

  // Kutu içerik kadar büyür (en fazla ~8 satır).
  useEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [text]);

  const addFiles = (files: File[]) => {
    for (const file of files) {
      if (file.size > MAX_BYTES) {
        toast.warning(t('chat.fileTooLarge', { name: file.name }));
        continue;
      }
      const id = crypto.randomUUID();
      const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
      setUploads((u) => [...u, { id, file, progress: 0, preview }]);
      chatApi
        .upload(file, (pct) => setUploads((u) => u.map((x) => (x.id === id ? { ...x, progress: pct } : x))))
        .then((result) => setUploads((u) => u.map((x) => (x.id === id ? { ...x, result, progress: 100 } : x))))
        .catch(() => setUploads((u) => u.map((x) => (x.id === id ? { ...x, error: true } : x))));
    }
  };

  useImperativeHandle(ref, () => ({ addFiles, focus: () => area.current?.focus() }));

  const busy = uploads.some((u) => !u.result && !u.error);
  const ready = uploads.filter((u) => u.result).map((u) => u.result!);
  const canSend = !p.disabled && !busy && (text.trim().length > 0 || ready.length > 0);

  const submit = async () => {
    if (p.editing) {
      const content = text.trim();
      if (!content) return;
      if (await p.onSaveEdit(p.editing, content)) setText('');
      return;
    }
    if (!canSend) return;
    const content = text.trim();
    const attachments = ready;
    setText('');
    uploads.forEach((u) => u.preview && URL.revokeObjectURL(u.preview));
    setUploads([]);
    const ok = await p.onSend(content, attachments);
    if (!ok && content) setText(content);
  };

  return (
    <div className="border-border bg-bg-elevated border-t p-2">
      {p.replyTo || p.editing ? (
        <div className="bg-bg-muted mb-1.5 flex items-start gap-2 rounded-lg px-2 py-1.5 text-xs">
          {p.editing ? <Pencil className="text-brand-600 mt-0.5 h-3.5 w-3.5 shrink-0" /> : null}
          <div className="border-brand-500 min-w-0 flex-1 border-l-2 pl-2">
            <span className="text-brand-600 block font-semibold">
              {p.editing ? t('chat.editing') : t('chat.replyingTo', { name: p.replyTo?.senderName ?? '' })}
            </span>
            <span className="text-fg-muted line-clamp-2">{(p.editing ?? p.replyTo)?.content || '…'}</span>
          </div>
          <button
            type="button"
            aria-label={t('common:Cancel')}
            onClick={() => {
              if (p.editing) {
                p.onCancelEdit();
                setText('');
              } else p.onCancelReply();
            }}
            className="text-fg-subtle hover:text-fg"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : null}

      {uploads.length ? (
        <div className="mb-1.5 flex gap-1.5 overflow-x-auto pb-1">
          {uploads.map((u) => (
            <div key={u.id} className={cn('border-border bg-bg-muted relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border', u.error && 'border-danger')}>
              {u.preview ? <img src={u.preview} alt="" className="h-full w-full object-cover" /> : (
                <div className="flex flex-col items-center px-1 text-center">
                  <FileText className="text-fg-muted h-5 w-5" />
                  <span className="w-14 truncate text-[9px]">{u.file.name}</span>
                  <span className="text-fg-subtle text-[9px]">{fileSize(u.file.size)}</span>
                </div>
              )}
              {!u.result && !u.error ? (
                <span className="absolute inset-x-0 bottom-0 h-1 bg-black/20">
                  <span className="bg-brand-500 block h-full transition-[width]" style={{ width: `${u.progress}%` }} />
                </span>
              ) : null}
              <button
                type="button"
                aria-label={t('chat.removeFile')}
                onClick={() => {
                  if (u.preview) URL.revokeObjectURL(u.preview);
                  setUploads((x) => x.filter((y) => y.id !== u.id));
                }}
                className="absolute top-0.5 right-0.5 rounded-full bg-black/55 p-0.5 text-white"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="flex items-end gap-1">
        {!p.editing ? (
          <>
            <button type="button" aria-label={t('chat.attach')} title={t('chat.attach')} disabled={p.disabled} onClick={() => fileInput.current?.click()} className="text-fg-muted hover:bg-bg-hover hover:text-fg rounded-full p-2">
              <Paperclip className="h-5 w-5" />
            </button>
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                addFiles(Array.from(e.target.files ?? []));
                e.target.value = '';
              }}
            />
          </>
        ) : null}
        <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
          <PopoverTrigger asChild>
            <button type="button" aria-label={t('chat.emoji')} disabled={p.disabled} className="text-fg-muted hover:bg-bg-hover hover:text-fg rounded-full p-2">
              <Smile className="h-5 w-5" />
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="grid w-64 grid-cols-8 gap-0.5 p-1.5" onOpenAutoFocus={(e) => e.preventDefault()}>
            {EMOJIS.map((e) => (
              <button
                key={e}
                type="button"
                className="hover:bg-bg-hover rounded p-1 text-lg leading-none"
                onClick={() => {
                  const el = area.current;
                  const start = el?.selectionStart ?? text.length;
                  const end = el?.selectionEnd ?? text.length;
                  setText(text.slice(0, start) + e + text.slice(end));
                  requestAnimationFrame(() => {
                    el?.focus();
                    el?.setSelectionRange(start + e.length, start + e.length);
                  });
                }}
              >
                {e}
              </button>
            ))}
          </PopoverContent>
        </Popover>
        <textarea
          ref={area}
          rows={1}
          value={text}
          disabled={p.disabled}
          placeholder={p.disabled ? t('chat.cannotWrite') : t('chat.placeholder')}
          onChange={(e) => {
            setText(e.target.value);
            // "Yazıyor…" en çok 3 sn'de bir bildirilir.
            if (!p.editing && Date.now() - lastTyping.current > 3000) {
              lastTyping.current = Date.now();
              p.onTyping();
            }
          }}
          onPaste={(e) => {
            const files = Array.from(e.clipboardData.files ?? []);
            if (files.length) {
              e.preventDefault();
              addFiles(files);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void submit();
            } else if (e.key === 'Escape') {
              if (p.editing) {
                p.onCancelEdit();
                setText('');
              } else if (p.replyTo) p.onCancelReply();
            }
          }}
          className="border-border bg-bg focus-visible:border-brand-500 max-h-44 min-h-10 flex-1 resize-none rounded-2xl border px-3.5 py-2 text-sm leading-5 outline-none"
        />
        <button
          type="button"
          aria-label={p.editing ? t('common:Save') : t('chat.send')}
          disabled={p.editing ? !text.trim() : !canSend}
          onClick={() => void submit()}
          className="bg-brand-600 hover:bg-brand-700 disabled:bg-bg-muted disabled:text-fg-subtle rounded-full p-2.5 text-white transition-colors"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
});
